<template>
  <div
    ref="root"
    class="register-desktop-select"
    :class="{
      'register-desktop-select--open': open,
      'register-desktop-select--invalid': invalid,
      'register-desktop-select--multiple': multiple,
      'register-desktop-select--filled': hasValue,
      'register-desktop-select--float-label': floatLabel,
    }"
  >
    <button
      :id="triggerId"
      type="button"
      class="register-desktop-select__trigger"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-haspopup="true"
      @click="toggle"
    >
      <span class="register-desktop-select__label">
        <slot name="label">
          {{ label }}<span v-if="required" class="register-desktop-select__req"> *</span>
        </slot>
      </span>

      <span class="register-desktop-select__value-wrap">
        <span v-if="multiple && selectedLabels.length" class="register-desktop-select__chips">
          <span
            v-for="chip in selectedLabels"
            :key="chip.value"
            class="register-desktop-select__chip"
          >
            <span class="register-desktop-select__chip-text">{{ chip.text }}</span>
            <button
              type="button"
              class="register-desktop-select__chip-remove"
              :aria-label="`Remove ${chip.text}`"
              @click.stop="removeValue(chip.value)"
            >
              <v-icon size="16" aria-hidden="true">{{ $icons.close }}</v-icon>
            </button>
          </span>
        </span>
        <span
          v-else-if="hasValue"
          class="register-desktop-select__value"
        >
          {{ singleDisplayText }}
        </span>
      </span>

      <span class="register-desktop-select__actions">
        <button
          v-if="clearable && hasValue"
          type="button"
          class="register-desktop-select__clear"
          aria-label="Clear selection"
          @click.stop="clearSelection"
        >
          <v-icon size="18" aria-hidden="true">{{ $icons.close }}</v-icon>
        </button>
        <v-icon class="register-desktop-select__chevron" size="20" aria-hidden="true">
          {{ $icons.chevronDown }}
        </v-icon>
      </span>
    </button>

    <transition name="register-desktop-dropdown">
      <div v-if="open" class="register-desktop-select__menu" role="listbox" :aria-multiselectable="multiple ? 'true' : 'false'">
        <div v-if="searchable" class="register-desktop-select__search-wrap">
          <v-icon class="register-desktop-select__search-icon" size="18" aria-hidden="true">{{ $icons.magnify }}</v-icon>
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="search"
            class="register-desktop-select__search"
            :placeholder="searchPlaceholder"
            autocomplete="off"
            @keydown.esc.prevent="close"
          />
        </div>

        <p v-if="multiple" class="register-desktop-select__hint">
          <template v-if="maxSelections">Select up to {{ maxSelections }}, then click outside to apply.</template>
          <template v-else>Select one or more, then click outside to apply.</template>
        </p>
        <p v-if="limitReached" class="register-desktop-select__limit-msg" role="alert">
          Maximum {{ maxSelections }} selections allowed.
        </p>

        <ul class="register-desktop-select__list">
          <template v-for="(item, index) in listItems">
            <li
              v-if="item.header"
              :key="`header-${item.header}-${index}`"
              class="register-desktop-select__group"
            >
              {{ item.header }}
            </li>
            <li v-else-if="item.divider" :key="`divider-${index}`" class="register-desktop-select__divider" role="separator" />
            <li v-else :key="`${getItemValue(item)}-${index}`">
              <button
                type="button"
                class="register-desktop-select__option"
                :class="{
                  'register-desktop-select__option--selected': isSelected(item),
                  'register-desktop-select__option--disabled': isOptionDisabled(item),
                }"
                role="option"
                :aria-selected="isSelected(item) ? 'true' : 'false'"
                :aria-disabled="isOptionDisabled(item) ? 'true' : 'false'"
                :disabled="isOptionDisabled(item)"
                @click="onOptionTap(item)"
              >
                <span class="register-desktop-select__option-text">
                  <span>{{ getItemText(item) }}</span>
                  <span v-if="item.group && isSearching" class="register-desktop-select__option-sub">
                    {{ item.group }}
                  </span>
                </span>
                <v-icon
                  v-if="multiple && isSelected(item)"
                  class="register-desktop-select__check"
                  size="18"
                  aria-hidden="true"
                >{{ $icons.check }}</v-icon>
              </button>
            </li>
          </template>
          <li v-if="!listItems.length" class="register-desktop-select__empty">No matches found</li>
        </ul>

        <div v-if="multiple && (selectedCount > 0 || maxSelections)" class="register-desktop-select__footer">
          <span class="register-desktop-select__count">
            {{ selectedCount }}<template v-if="maxSelections"> / {{ maxSelections }}</template> selected
          </span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'RegisterDesktopSelect',
  props: {
    value: {
      default: null,
    },
    items: {
      type: Array,
      default: () => [],
    },
    itemValue: {
      type: String,
      default: 'value',
    },
    itemText: {
      type: String,
      default: 'text',
    },
    label: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: 'Select an option',
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: '',
    },
    maxSelections: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      open: false,
      searchQuery: '',
      limitReached: false,
    };
  },
  computed: {
    triggerId() {
      return this.id || `register-desktop-select-${this._uid}`;
    },
    floatLabel() {
      return this.open || this.hasValue;
    },
    searchPlaceholder() {
      return this.searchable ? 'Search…' : '';
    },
    isSearching() {
      return this.searchQuery.trim().length > 0;
    },
    selectableItems() {
      return (this.items || []).filter(
        (item) => item && !item.header && !item.divider && this.getItemValue(item) != null && this.getItemValue(item) !== '',
      );
    },
    listItems() {
      if (!this.isSearching) return this.items || [];
      const query = this.searchQuery.trim().toLowerCase();
      return this.selectableItems.filter((item) =>
        String(this.getItemText(item)).toLowerCase().includes(query)
        || (item.group && String(item.group).toLowerCase().includes(query)),
      );
    },
    hasValue() {
      if (this.multiple) return Array.isArray(this.value) && this.value.length > 0;
      return this.value !== null && this.value !== undefined && this.value !== '';
    },
    selectedCount() {
      return Array.isArray(this.value) ? this.value.length : 0;
    },
    isAtMax() {
      if (!this.multiple || this.maxSelections == null) return false;
      return this.selectedCount >= this.maxSelections;
    },
    selectedLabels() {
      if (!this.multiple || !Array.isArray(this.value)) return [];
      return this.value
        .map((val) => {
          const match = this.selectableItems.find((item) => this.getItemValue(item) === val);
          return match ? { value: val, text: this.getItemText(match) } : null;
        })
        .filter(Boolean);
    },
    singleDisplayText() {
      if (!this.hasValue) return '';
      const selected = this.selectableItems.find((item) => this.getItemValue(item) === this.value);
      return selected ? this.getItemText(selected) : '';
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);
  },
  methods: {
    getItemValue(item) {
      return item?.[this.itemValue];
    },
    getItemText(item) {
      return item?.[this.itemText] ?? '';
    },
    isSelected(item) {
      const val = this.getItemValue(item);
      if (this.multiple) return Array.isArray(this.value) && this.value.includes(val);
      return this.value === val;
    },
    toggle() {
      if (this.open) {
        this.close();
      } else {
        this.openMenu();
      }
    },
    openMenu() {
      this.searchQuery = '';
      this.limitReached = false;
      this.open = true;
      this.$nextTick(() => {
        if (this.searchable && this.$refs.searchInput) {
          this.$refs.searchInput.focus();
        }
      });
    },
    close() {
      if (!this.open) return;
      this.open = false;
      this.searchQuery = '';
      this.limitReached = false;
      this.$emit('change', this.value);
    },
    onDocumentClick(event) {
      if (!this.open || !this.$refs.root) return;
      if (!this.$refs.root.contains(event.target)) {
        this.close();
      }
    },
    onDocumentKeydown(event) {
      if (event.key === 'Escape' && this.open) {
        this.close();
      }
    },
    clearSelection() {
      const cleared = this.multiple ? [] : null;
      this.$emit('input', cleared);
      this.$emit('change', cleared);
    },
    removeValue(val) {
      if (!this.multiple || !Array.isArray(this.value)) return;
      const next = this.value.filter((item) => item !== val);
      this.limitReached = false;
      this.$emit('input', next);
    },
    isOptionDisabled(item) {
      if (!this.isAtMax) return false;
      return !this.isSelected(item);
    },
    onOptionTap(item) {
      if (this.isOptionDisabled(item)) {
        this.limitReached = true;
        return;
      }
      const val = this.getItemValue(item);
      if (this.multiple) {
        const current = Array.isArray(this.value) ? [...this.value] : [];
        const index = current.indexOf(val);
        if (index > -1) {
          current.splice(index, 1);
          this.limitReached = false;
        } else if (this.maxSelections != null && current.length >= this.maxSelections) {
          this.limitReached = true;
          return;
        } else {
          current.push(val);
          this.limitReached = false;
        }
        this.$emit('input', current);
        return;
      }
      this.$emit('input', val);
      this.close();
    },
  },
};
</script>
